export type Sex = 'male' | 'female';

export type Ethnicity = 
  | 'caucasian'
  | 'african'
  | 'asian'
  | 'hispanic'
  | 'middle-eastern'
  | 'south-asian'
  | 'mixed'
  | 'other';

export type SkinColor = 
  | 'very-fair'
  | 'fair'
  | 'medium'
  | 'olive'
  | 'brown'
  | 'dark-brown'
  | 'very-dark';

export interface UserData {
  sex: Sex;
  age: number;
  ethnicity: Ethnicity;
  skinColor: SkinColor;
  heightCm: number;
  weightKg: number;
}

export interface PhotoData {
  frontPhoto: string | null;
  sidePhoto: string | null;
}

// Landmark point on image (relative coordinates 0-1)
export interface LandmarkPoint {
  x: number;
  y: number;
}

// All facial landmarks that need to be marked
export interface FacialLandmarks {
  // Eyes
  leftEyeInner: LandmarkPoint | null;
  leftEyeOuter: LandmarkPoint | null;
  leftEyeTop: LandmarkPoint | null;
  leftEyeBottom: LandmarkPoint | null;
  rightEyeInner: LandmarkPoint | null;
  rightEyeOuter: LandmarkPoint | null;
  rightEyeTop: LandmarkPoint | null;
  rightEyeBottom: LandmarkPoint | null;
  leftPupil: LandmarkPoint | null;
  rightPupil: LandmarkPoint | null;
  
  // Eyebrows
  leftBrowInner: LandmarkPoint | null;
  leftBrowOuter: LandmarkPoint | null;
  leftBrowPeak: LandmarkPoint | null;
  rightBrowInner: LandmarkPoint | null;
  rightBrowOuter: LandmarkPoint | null;
  rightBrowPeak: LandmarkPoint | null;
  
  // Nose
  nasion: LandmarkPoint | null; // bridge of nose between eyes
  noseTip: LandmarkPoint | null;
  noseLeftAla: LandmarkPoint | null; // left nostril edge
  noseRightAla: LandmarkPoint | null;
  subnasale: LandmarkPoint | null; // bottom of nose where it meets philtrum
  
  // Lips
  lipTopCenter: LandmarkPoint | null;
  lipBottomCenter: LandmarkPoint | null;
  lipLeftCorner: LandmarkPoint | null;
  lipRightCorner: LandmarkPoint | null;
  cupidsBowLeft: LandmarkPoint | null;
  cupidsBowRight: LandmarkPoint | null;
  
  // Face structure
  foreheadCenter: LandmarkPoint | null;
  hairlineCenter: LandmarkPoint | null;
  glabella: LandmarkPoint | null; // between eyebrows
  chinBottom: LandmarkPoint | null;
  chinTip: LandmarkPoint | null;
  jawLeftAngle: LandmarkPoint | null; // gonion
  jawRightAngle: LandmarkPoint | null;
  cheekboneLeft: LandmarkPoint | null;
  cheekboneRight: LandmarkPoint | null;
  
  // Face width points
  templeLeft: LandmarkPoint | null;
  templeRight: LandmarkPoint | null;
  faceLeftEdge: LandmarkPoint | null; // at cheekbone level
  faceRightEdge: LandmarkPoint | null;
  
  // Philtrum
  philtrumTop: LandmarkPoint | null;
  philtrumBottom: LandmarkPoint | null;
  
  // Eyelids (for exposure measurement)
  leftUpperLidCrease: LandmarkPoint | null;
  rightUpperLidCrease: LandmarkPoint | null;
  leftLowerLashLine: LandmarkPoint | null;
  rightLowerLashLine: LandmarkPoint | null;
  
  // Additional measurements
  leftTear: LandmarkPoint | null; // tear duct/inner canthus
  rightTear: LandmarkPoint | null;
}

// Profile landmarks for side view
export interface ProfileLandmarks {
  foreheadTop: LandmarkPoint | null;
  glabella: LandmarkPoint | null;
  nasion: LandmarkPoint | null;
  noseBridge: LandmarkPoint | null;
  noseTip: LandmarkPoint | null;
  subnasale: LandmarkPoint | null;
  lipTop: LandmarkPoint | null;
  lipBottom: LandmarkPoint | null;
  chinTip: LandmarkPoint | null;
  chinBottom: LandmarkPoint | null;
  jawAngle: LandmarkPoint | null; // gonion
  earTop: LandmarkPoint | null;
  earBottom: LandmarkPoint | null;
  neckStart: LandmarkPoint | null;
  throatPoint: LandmarkPoint | null;
}

// User-provided feature characteristics
export interface FeatureCharacteristics {
  eyeColor: 'blue' | 'green' | 'hazel' | 'brown' | 'dark-brown' | 'black' | 'gray' | 'amber';
  hairColor: 'blonde' | 'light-brown' | 'brown' | 'dark-brown' | 'black' | 'red' | 'gray' | 'white';
  hairType: 'straight' | 'wavy' | 'curly' | 'coily' | 'bald' | 'thinning';
  facialHair: 'none' | 'stubble' | 'beard' | 'mustache' | 'goatee' | 'full-beard';
}

// Individual feature type classifications
export type EyeType = 'almond' | 'round' | 'hooded' | 'monolid' | 'downturned' | 'upturned' | 'hunter' | 'prey' | 'deep-set' | 'protruding' | 'close-set' | 'wide-set';
export type EyebrowType = 'arched' | 'straight' | 'rounded' | 's-shaped' | 'flat' | 'thick' | 'thin' | 'sparse';
export type NoseType = 'straight' | 'roman' | 'button' | 'snub' | 'hawk' | 'greek' | 'nubian' | 'bulbous' | 'fleshy' | 'celestial';
export type LipType = 'full' | 'thin' | 'heart-shaped' | 'wide' | 'round' | 'bow-shaped' | 'downturned' | 'heavy-upper' | 'heavy-lower';
export type JawType = 'square' | 'rounded' | 'pointed' | 'heart' | 'oval' | 'strong' | 'weak' | 'recessed' | 'protruding' | 'asymmetric';
export type ChinType = 'strong' | 'weak' | 'recessed' | 'protruding' | 'cleft' | 'pointed' | 'square' | 'round' | 'double';
export type CheekboneType = 'high' | 'low' | 'prominent' | 'flat' | 'wide' | 'narrow' | 'defined' | 'soft';
export type ForeheadType = 'high' | 'low' | 'wide' | 'narrow' | 'flat' | 'rounded' | 'sloped' | 'prominent-brow';
export type FaceShapeType = 'oval' | 'round' | 'square' | 'heart' | 'oblong' | 'diamond' | 'triangle' | 'rectangle';
export type RamusType = 'long' | 'short' | 'average' | 'prominent' | 'recessed';

// Male Archetypes based on dimorphism and youth indicators
export type MaleArchetype = 'cutecel' | 'prettyboy' | 'robust-prettyboy' | 'maesthetic' | 'dom' | 'ogre';
export type FemaleArchetype = 'cute' | 'pretty' | 'elegant' | 'striking' | 'classic' | 'fierce';

export interface ArchetypeResult {
  type: MaleArchetype | FemaleArchetype;
  score: number;
  description: string;
  strengths: string[];
  considerations: string[];
}

// Individual feature rating with type classification
export interface FeatureRating {
  feature: string;
  type: string;
  score: number; // 0-100
  tier: number; // 1-8
  category: 'excellent' | 'good' | 'average' | 'below' | 'poor';
  description: string;
  measurements?: {
    name: string;
    value: number;
    ideal: string;
  }[];
}

// PSL Tier naming
export type PSLTier = 
  | 'Sub1' | 'Sub2' | 'Sub3' | 'Sub4' | 'Sub5'
  | 'LTN' | 'LTB'
  | 'MTN' | 'MTB'
  | 'HTN' | 'HTB'
  | 'Chadlite' | 'Stacylite'
  | 'Chad' | 'Stacy'
  | 'Adamlite' | 'Evelite'
  | 'TrueAdam' | 'TrueEve';

export interface RatioResult {
  name: string;
  value: number;
  idealMin: number;
  idealMax: number;
  score: number; // 0-100
  tier: number; // 1-8 tier
  category: 'excellent' | 'good' | 'average' | 'below' | 'poor';
}

export interface CategoryScore {
  name: string;
  abbreviation: string;
  score: number; // 0-100
  weight: number;
  description: string;
  ratios: RatioResult[];
}

export interface FinalScores {
  harmScore: number; // Harmony 32%
  dimoScore: number; // Dimorphism 20%
  anguScore: number; // Angularity 22%
  miscScore: number; // Miscellaneous 26%
  
  weightedAverage: number;
  spreadPenalty: number;
  trueScore: number; // Final TRUE SCORE
  
  pslScore: number; // 1-8 scale
  attractivenessScore: number; // 1-10 scale
  goldenRatioPercent: number;
  smvScore: number;
  
  tier: PSLTier;
  rarity: string; // e.g., "1 in 4,100"
}

export interface FeatureAnalysis {
  name: string;
  rating: 'halo' | 'neutral' | 'failo';
  score: number;
  description: string;
}

// Validation result for landmark placement
export interface LandmarkValidationInfo {
  isValid: boolean;
  overallScore: number;
  confidenceLevel: 'high' | 'medium' | 'low' | 'invalid';
  errorCount: number;
  warningCount: number;
  summary: string;
}

export interface AnalysisResult {
  id: string;
  timestamp: Date;
  userData: UserData;
  photos: PhotoData;
  
  // Landmarks used for calculation
  facialLandmarks: FacialLandmarks;
  profileLandmarks: ProfileLandmarks | null;
  featureCharacteristics: FeatureCharacteristics;
  
  frontalRatios: RatioResult[];
  profileRatios: RatioResult[];
  
  // Individual feature ratings
  featureRatings: FeatureRating[];
  
  // Archetype classification
  archetype: ArchetypeResult;
  
  categoryScores: CategoryScore[];
  finalScores: FinalScores;
  
  halos: FeatureAnalysis[];
  failos: FeatureAnalysis[];
  
  isQuickScan: boolean;
  
  // Validation info for landmark placement accuracy
  validationInfo?: LandmarkValidationInfo;
}

// Landmark definition for UI
export interface LandmarkDefinition {
  key: keyof FacialLandmarks | keyof ProfileLandmarks;
  label: string;
  description: string;
  group: 'eyes' | 'eyebrows' | 'nose' | 'lips' | 'face-structure' | 'profile';
  required: boolean;
}

// Frontal landmarks to mark
export const FRONTAL_LANDMARK_DEFINITIONS: LandmarkDefinition[] = [
  // Eyes
  { key: 'leftEyeInner', label: 'Left Eye Inner Corner', description: 'Inner corner of left eye (tear duct)', group: 'eyes', required: true },
  { key: 'leftEyeOuter', label: 'Left Eye Outer Corner', description: 'Outer corner of left eye', group: 'eyes', required: true },
  { key: 'leftEyeTop', label: 'Left Eye Top', description: 'Top of left iris/eye opening', group: 'eyes', required: true },
  { key: 'leftEyeBottom', label: 'Left Eye Bottom', description: 'Bottom of left eye opening', group: 'eyes', required: true },
  { key: 'rightEyeInner', label: 'Right Eye Inner Corner', description: 'Inner corner of right eye', group: 'eyes', required: true },
  { key: 'rightEyeOuter', label: 'Right Eye Outer Corner', description: 'Outer corner of right eye', group: 'eyes', required: true },
  { key: 'rightEyeTop', label: 'Right Eye Top', description: 'Top of right iris/eye opening', group: 'eyes', required: true },
  { key: 'rightEyeBottom', label: 'Right Eye Bottom', description: 'Bottom of right eye opening', group: 'eyes', required: true },
  
  // Eyebrows
  { key: 'leftBrowInner', label: 'Left Brow Start', description: 'Inner start of left eyebrow', group: 'eyebrows', required: true },
  { key: 'leftBrowOuter', label: 'Left Brow End', description: 'Outer end of left eyebrow', group: 'eyebrows', required: true },
  { key: 'leftBrowPeak', label: 'Left Brow Peak', description: 'Highest point of left eyebrow arch', group: 'eyebrows', required: false },
  { key: 'rightBrowInner', label: 'Right Brow Start', description: 'Inner start of right eyebrow', group: 'eyebrows', required: true },
  { key: 'rightBrowOuter', label: 'Right Brow End', description: 'Outer end of right eyebrow', group: 'eyebrows', required: true },
  { key: 'rightBrowPeak', label: 'Right Brow Peak', description: 'Highest point of right eyebrow arch', group: 'eyebrows', required: false },
  
  // Nose
  { key: 'nasion', label: 'Nasion (Nose Bridge)', description: 'Bridge of nose between eyes', group: 'nose', required: true },
  { key: 'noseTip', label: 'Nose Tip', description: 'Tip of the nose', group: 'nose', required: true },
  { key: 'noseLeftAla', label: 'Left Nostril Edge', description: 'Left edge of nostril/ala', group: 'nose', required: true },
  { key: 'noseRightAla', label: 'Right Nostril Edge', description: 'Right edge of nostril/ala', group: 'nose', required: true },
  { key: 'subnasale', label: 'Subnasale', description: 'Bottom of nose where it meets upper lip area', group: 'nose', required: true },
  
  // Lips
  { key: 'lipTopCenter', label: 'Upper Lip Center', description: 'Center top of upper lip', group: 'lips', required: true },
  { key: 'lipBottomCenter', label: 'Lower Lip Center', description: 'Center bottom of lower lip', group: 'lips', required: true },
  { key: 'lipLeftCorner', label: 'Left Lip Corner', description: 'Left corner of mouth', group: 'lips', required: true },
  { key: 'lipRightCorner', label: 'Right Lip Corner', description: 'Right corner of mouth', group: 'lips', required: true },
  
  // Face structure
  { key: 'hairlineCenter', label: 'Hairline Center', description: 'Center of hairline at forehead', group: 'face-structure', required: true },
  { key: 'glabella', label: 'Glabella', description: 'Point between eyebrows', group: 'face-structure', required: true },
  { key: 'chinBottom', label: 'Chin Bottom', description: 'Bottom-most point of chin', group: 'face-structure', required: true },
  { key: 'jawLeftAngle', label: 'Left Jaw Angle', description: 'Left jaw angle (gonion)', group: 'face-structure', required: true },
  { key: 'jawRightAngle', label: 'Right Jaw Angle', description: 'Right jaw angle (gonion)', group: 'face-structure', required: true },
  { key: 'cheekboneLeft', label: 'Left Cheekbone', description: 'Widest point of left cheekbone', group: 'face-structure', required: true },
  { key: 'cheekboneRight', label: 'Right Cheekbone', description: 'Widest point of right cheekbone', group: 'face-structure', required: true },
  { key: 'templeLeft', label: 'Left Temple', description: 'Left temple at widest point', group: 'face-structure', required: false },
  { key: 'templeRight', label: 'Right Temple', description: 'Right temple at widest point', group: 'face-structure', required: false },
];

export const PROFILE_LANDMARK_DEFINITIONS: LandmarkDefinition[] = [
  { key: 'foreheadTop', label: 'Forehead Top', description: 'Highest point of forehead', group: 'profile', required: true },
  { key: 'glabella', label: 'Glabella', description: 'Most prominent point between brows', group: 'profile', required: true },
  { key: 'nasion', label: 'Nasion', description: 'Deepest point of nose bridge', group: 'profile', required: true },
  { key: 'noseTip', label: 'Nose Tip', description: 'Most protruding point of nose', group: 'profile', required: true },
  { key: 'subnasale', label: 'Subnasale', description: 'Where nose meets upper lip', group: 'profile', required: true },
  { key: 'lipTop', label: 'Upper Lip', description: 'Most prominent point of upper lip', group: 'profile', required: true },
  { key: 'lipBottom', label: 'Lower Lip', description: 'Most prominent point of lower lip', group: 'profile', required: true },
  { key: 'chinTip', label: 'Chin Tip', description: 'Most prominent point of chin', group: 'profile', required: true },
  { key: 'chinBottom', label: 'Chin Bottom', description: 'Lowest point of chin', group: 'profile', required: true },
  { key: 'jawAngle', label: 'Jaw Angle', description: 'Angle of the jaw (gonion)', group: 'profile', required: true },
  { key: 'earTop', label: 'Ear Top', description: 'Top of ear', group: 'profile', required: false },
  { key: 'neckStart', label: 'Neck Start', description: 'Where neck meets jaw', group: 'profile', required: false },
];

// Initialize empty landmarks
export function createEmptyFacialLandmarks(): FacialLandmarks {
  return {
    leftEyeInner: null,
    leftEyeOuter: null,
    leftEyeTop: null,
    leftEyeBottom: null,
    rightEyeInner: null,
    rightEyeOuter: null,
    rightEyeTop: null,
    rightEyeBottom: null,
    leftPupil: null,
    rightPupil: null,
    leftBrowInner: null,
    leftBrowOuter: null,
    leftBrowPeak: null,
    rightBrowInner: null,
    rightBrowOuter: null,
    rightBrowPeak: null,
    nasion: null,
    noseTip: null,
    noseLeftAla: null,
    noseRightAla: null,
    subnasale: null,
    lipTopCenter: null,
    lipBottomCenter: null,
    lipLeftCorner: null,
    lipRightCorner: null,
    cupidsBowLeft: null,
    cupidsBowRight: null,
    foreheadCenter: null,
    hairlineCenter: null,
    glabella: null,
    chinBottom: null,
    chinTip: null,
    jawLeftAngle: null,
    jawRightAngle: null,
    cheekboneLeft: null,
    cheekboneRight: null,
    templeLeft: null,
    templeRight: null,
    faceLeftEdge: null,
    faceRightEdge: null,
    philtrumTop: null,
    philtrumBottom: null,
    leftUpperLidCrease: null,
    rightUpperLidCrease: null,
    leftLowerLashLine: null,
    rightLowerLashLine: null,
    leftTear: null,
    rightTear: null,
  };
}

export function createEmptyProfileLandmarks(): ProfileLandmarks {
  return {
    foreheadTop: null,
    glabella: null,
    nasion: null,
    noseBridge: null,
    noseTip: null,
    subnasale: null,
    lipTop: null,
    lipBottom: null,
    chinTip: null,
    chinBottom: null,
    jawAngle: null,
    earTop: null,
    earBottom: null,
    neckStart: null,
    throatPoint: null,
  };
}

export const DEFAULT_FEATURE_CHARACTERISTICS: FeatureCharacteristics = {
  eyeColor: 'brown',
  hairColor: 'brown',
  hairType: 'straight',
  facialHair: 'none',
};
